import React, { useEffect, useState } from 'react';
import api, { unwrap } from '../utils/api';
import Icon, { withCheck } from '../components/Icon';

export default function Dashboard() {
  const [counts, setCounts] = useState({ products: 0, orders: 0, coupons: 0, fonts: 0 });
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '' });
  const [savingMx, setSavingMx] = useState(false);
  const [mxMsg, setMxMsg] = useState('');
  const [devBlock, setDevBlock] = useState({ enabled: true });
  const [savingDev, setSavingDev] = useState(false);
  const [devMsg, setDevMsg] = useState('');
  const [announce, setAnnounce] = useState({ enabled: false, message: '', version: 0 });
  const [savingAnn, setSavingAnn] = useState(false);
  const [annMsg, setAnnMsg] = useState('');
  const [cursor, setCursor] = useState({ disableCustomCursor: false, disableEasing: false });
  const [savingCursor, setSavingCursor] = useState(false);
  const [cursorMsg, setCursorMsg] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/products/GetAllProducts?limit=1'),
      api.get('/orders/allgetOrder?limit=1').catch(() => ({ data: { body: { total: 0 } } })),
      api.get('/coupons/getCoupons'),
      api.get('/files/GetAllFonts'),
      api.get('/miscellaneous/maintenance').catch(() => ({ data: { body: { enabled: false } } })),
      api.get('/miscellaneous/devToolsBlock').catch(() => ({ data: { body: { enabled: true } } })),
      api.get('/miscellaneous/announcement').catch(() => ({ data: { body: { enabled: false, message: '', version: 0 } } })),
      api.get('/miscellaneous/cursorSettings').catch(() => ({ data: { body: { disableCustomCursor: false, disableEasing: false } } })),
    ]).then(([p, o, c, f, m, d, a, cs]) => {
      setCounts({
        products: unwrap(p)?.total || 0,
        orders: unwrap(o)?.total || 0,
        coupons: (unwrap(c) || []).length,
        fonts: (unwrap(f) || []).length,
      });
      const mx = unwrap(m) || { enabled: false };
      setMaintenance({ enabled: !!mx.enabled, message: mx.message || '' });
      const dv = unwrap(d) || { enabled: true };
      setDevBlock({ enabled: dv.enabled !== false });
      const an = unwrap(a) || { enabled: false, message: '', version: 0 };
      setAnnounce({ enabled: !!an.enabled, message: an.message || '', version: Number(an.version) || 0 });
      const cur = unwrap(cs) || {};
      setCursor({
        disableCustomCursor: !!cur.disableCustomCursor,
        disableEasing: !!cur.disableEasing,
      });
    });
  }, []);

  async function saveCursor(next) {
    setSavingCursor(true);
    setCursorMsg('');
    try {
      const r = await api.post('/miscellaneous/cursorSettings', next);
      const saved = unwrap(r) || next;
      setCursor({
        disableCustomCursor: !!saved.disableCustomCursor,
        disableEasing: !!saved.disableEasing,
      });
      setCursorMsg('✓ Saved. Public visitors will pick up the change on their next page load.');
    } catch (e) {
      setCursorMsg('Save failed: ' + (e?.response?.data?.body || e.message));
    } finally {
      setSavingCursor(false);
      setTimeout(() => setCursorMsg(''), 4000);
    }
  }

  async function saveAnnouncement(next) {
    setSavingAnn(true);
    setAnnMsg('');
    try {
      const r = await api.post('/miscellaneous/announcement', next);
      const saved = unwrap(r) || next;
      setAnnounce({ enabled: !!saved.enabled, message: saved.message || '', version: Number(saved.version) || 0 });
      setAnnMsg(saved.enabled ? '✓ Announcement is LIVE on the public site.' : '✓ Announcement is OFF.');
    } catch (e) {
      setAnnMsg('Save failed: ' + (e?.response?.data?.body || e.message));
    } finally {
      setSavingAnn(false);
      setTimeout(() => setAnnMsg(''), 4000);
    }
  }

  async function saveDevBlock(next) {
    setSavingDev(true);
    setDevMsg('');
    try {
      const r = await api.post('/miscellaneous/devToolsBlock', next);
      const saved = unwrap(r) || next;
      setDevBlock({ enabled: saved.enabled !== false });
      setDevMsg(saved.enabled ? '✓ Dev tools are BLOCKED on the public site.' : '✓ Dev tools are accessible on the public site.');
    } catch (e) {
      setDevMsg('Save failed: ' + (e?.response?.data?.body || e.message));
    } finally {
      setSavingDev(false);
      setTimeout(() => setDevMsg(''), 4000);
    }
  }

  async function saveMaintenance(next) {
    setSavingMx(true);
    setMxMsg('');
    try {
      const r = await api.post('/miscellaneous/maintenance', next);
      const saved = unwrap(r) || next;
      setMaintenance({ enabled: !!saved.enabled, message: saved.message || '' });
      setMxMsg(saved.enabled ? '✓ Site is now LOCKED for the public.' : '✓ Site is live for the public.');
    } catch (e) {
      setMxMsg('Save failed: ' + (e?.response?.data?.body || e.message));
    } finally {
      setSavingMx(false);
      setTimeout(() => setMxMsg(''), 4000);
    }
  }

  return (
    <>
      <h1>Dashboard</h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20 }}>
        {[
          ['Products', counts.products],
          ['Orders', counts.orders],
          ['Coupons', counts.coupons],
          ['Fonts', counts.fonts],
        ].map(([label, n]) => (
          <div className="card" key={label}>
            <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase' }}>{label}</div>
            <div style={{ fontSize: 36, fontWeight: 700 }}>{n}</div>
          </div>
        ))}
      </div>

      <div
        className="card"
        style={{
          marginTop: 28,
          background: maintenance.enabled ? '#fff5f5' : '#fff',
          border: maintenance.enabled ? '2px solid #f4a4a4' : '1px solid #e6eaef',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Public website</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: maintenance.enabled ? '#b42318' : '#0a7a32', marginTop: 4 }}>
              {maintenance.enabled
                ? <><Icon name="lock" style={{ marginRight: 6 }} />LOCKED — under maintenance</>
                : <><Icon name="radio_button_checked" style={{ marginRight: 6 }} />Live</>}
            </div>
            <p style={{ margin: '6px 0 0', color: '#666', fontSize: 13, maxWidth: 540 }}>
              When ON, anyone visiting the public site sees only a logo and a "site is under maintenance" message. You can still view the site yourself with the button below — the admin panel always stays accessible.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
            {/* Always-available "view site" — appends ?preview=1 so the gate
                stays bypassed for the admin's session even when LOCKED. */}
            <a
              href={`${process.env.REACT_APP_SITE_URL || 'http://localhost:3000'}/?preview=1`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn small secondary"
              title="Open the public site in a new tab. Bypasses maintenance mode for your session."
              style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              <Icon name="language" style={{ marginRight: 4 }} />View site
            </a>

            {/* iOS-style toggle */}
            <button
              type="button"
              onClick={() => saveMaintenance({ enabled: !maintenance.enabled, message: maintenance.message })}
              disabled={savingMx}
              aria-pressed={maintenance.enabled}
              title={maintenance.enabled ? 'Turn site back on for the public' : 'Lock the public site'}
              style={{
                position: 'relative',
                width: 64,
                height: 36,
                border: 'none',
                borderRadius: 18,
                background: maintenance.enabled ? '#b42318' : '#cbd2d9',
                cursor: savingMx ? 'wait' : 'pointer',
                transition: 'background 0.15s',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 3,
                  left: maintenance.enabled ? 31 : 3,
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                  transition: 'left 0.18s',
                }}
              />
            </button>
          </div>
        </div>

        <div className="prop" style={{ marginTop: 14 }}>
          <label style={{ fontSize: 13, color: '#666' }}>Optional message shown to visitors (leave blank for the default)</label>
          <input
            type="text"
            value={maintenance.message}
            onChange={e => setMaintenance(m => ({ ...m, message: e.target.value }))}
            onBlur={() => saveMaintenance(maintenance)}
            placeholder="We'll be back soon."
            style={{ marginTop: 4 }}
          />
        </div>

        {mxMsg && (
          <div style={{ marginTop: 10, fontSize: 13, color: mxMsg.startsWith('✓') ? '#198754' : '#dc3545' }}>{withCheck(mxMsg)}</div>
        )}
      </div>

      <div
        className="card"
        style={{
          marginTop: 20,
          background: devBlock.enabled ? '#f0f7ff' : '#fff',
          border: devBlock.enabled ? '2px solid #a4c6f4' : '1px solid #e6eaef',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Browser dev tools</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: devBlock.enabled ? '#0a5fb4' : '#666', marginTop: 4 }}>
              {devBlock.enabled
                ? <><Icon name="lock" style={{ marginRight: 6 }} />BLOCKED on the public site</>
                : <><Icon name="lock_open" style={{ marginRight: 6 }} />Accessible on the public site</>}
            </div>
            <p style={{ margin: '6px 0 0', color: '#666', fontSize: 13, maxWidth: 540 }}>
              When ON, customers can't open browser dev tools via F12, Ctrl+Shift+I/J/C, Ctrl+U, or right-click → Inspect. Turn it OFF temporarily when you need to debug the live site, then turn it back ON.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => saveDevBlock({ enabled: !devBlock.enabled })}
              disabled={savingDev}
              aria-pressed={devBlock.enabled}
              title={devBlock.enabled ? 'Allow dev tools on the public site' : 'Block dev tools on the public site'}
              style={{
                position: 'relative',
                width: 64,
                height: 36,
                border: 'none',
                borderRadius: 18,
                background: devBlock.enabled ? '#0a5fb4' : '#cbd2d9',
                cursor: savingDev ? 'wait' : 'pointer',
                transition: 'background 0.15s',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 3,
                  left: devBlock.enabled ? 31 : 3,
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                  transition: 'left 0.18s',
                }}
              />
            </button>
          </div>
        </div>

        {devMsg && (
          <div style={{ marginTop: 10, fontSize: 13, color: devMsg.startsWith('✓') ? '#198754' : '#dc3545' }}>{withCheck(devMsg)}</div>
        )}
      </div>

      <div
        className="card"
        style={{
          marginTop: 20,
          background: announce.enabled ? '#fffbe6' : '#fff',
          border: announce.enabled ? '2px solid #f0d97a' : '1px solid #e6eaef',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Announcement popup</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: announce.enabled ? '#8a6d00' : '#666', marginTop: 4 }}>
              {announce.enabled
                ? <><Icon name="campaign" style={{ marginRight: 6 }} />Showing on the public site</>
                : <><Icon name="visibility_off" style={{ marginRight: 6 }} />Hidden</>}
            </div>
            <p style={{ margin: '6px 0 0', color: '#666', fontSize: 13, maxWidth: 540 }}>
              When ON, a small dismissable popup appears when a visitor first lands on the site. Each visitor only sees it once per browser session — closing it hides it until they reopen the site. Editing the message resets the dismissal so returning visitors see the new note.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => saveAnnouncement({ enabled: !announce.enabled, message: announce.message })}
              disabled={savingAnn}
              aria-pressed={announce.enabled}
              title={announce.enabled ? 'Hide the announcement popup' : 'Show the announcement popup'}
              style={{
                position: 'relative',
                width: 64,
                height: 36,
                border: 'none',
                borderRadius: 18,
                background: announce.enabled ? '#d4a017' : '#cbd2d9',
                cursor: savingAnn ? 'wait' : 'pointer',
                transition: 'background 0.15s',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 3,
                  left: announce.enabled ? 31 : 3,
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                  transition: 'left 0.18s',
                }}
              />
            </button>
          </div>
        </div>

        <div className="prop" style={{ marginTop: 14 }}>
          <label style={{ fontSize: 13, color: '#666' }}>Message shown to visitors</label>
          <textarea
            value={announce.message}
            onChange={e => setAnnounce(a => ({ ...a, message: e.target.value }))}
            onBlur={() => saveAnnouncement(announce)}
            placeholder="e.g. New designs for Purim are now live!"
            rows={3}
            style={{ marginTop: 4, width: '100%', resize: 'vertical' }}
          />
        </div>

        {annMsg && (
          <div style={{ marginTop: 10, fontSize: 13, color: annMsg.startsWith('✓') ? '#198754' : '#dc3545' }}>{withCheck(annMsg)}</div>
        )}
      </div>

      <div
        className="card"
        style={{
          marginTop: 20,
          background: (cursor.disableCustomCursor || cursor.disableEasing) ? '#f6f3ff' : '#fff',
          border: (cursor.disableCustomCursor || cursor.disableEasing) ? '2px solid #c2b3f0' : '1px solid #e6eaef',
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Cursor</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>Custom cursor on the public site</div>
          <p style={{ margin: '6px 0 0', color: '#666', fontSize: 13, maxWidth: 600 }}>
            The site normally shows a custom blue arrow that smoothly follows the mouse. Use these switches to disable either piece for everyone. Mobile/tablet visitors always see the platform cursor regardless.
          </p>
        </div>

        <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            {
              key: 'disableCustomCursor',
              title: 'Disable custom cursor',
              when: cursor.disableCustomCursor,
              activeLabel: 'Showing the platform cursor',
              idleLabel: 'Showing the custom arrow',
              help: 'When ON, visitors see their normal operating-system cursor instead of the custom arrow.',
            },
            {
              key: 'disableEasing',
              title: 'Disable cursor easing',
              when: cursor.disableEasing,
              activeLabel: 'No smoothing — cursor snaps to the pointer',
              idleLabel: 'Smoothed — cursor lerps toward the pointer',
              help: 'When ON, the custom arrow follows the mouse exactly each frame instead of lagging slightly behind. Has no effect if the custom cursor is also disabled.',
            },
          ].map(row => (
            <div key={row.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{row.title}</div>
                <div style={{ fontSize: 13, color: row.when ? '#5a3eb0' : '#666', marginTop: 2 }}>
                  {row.when ? row.activeLabel : row.idleLabel}
                </div>
                <p style={{ margin: '4px 0 0', color: '#888', fontSize: 12, maxWidth: 480 }}>{row.help}</p>
              </div>

              <button
                type="button"
                onClick={() => saveCursor({ ...cursor, [row.key]: !cursor[row.key] })}
                disabled={savingCursor}
                aria-pressed={row.when}
                title={row.when ? 'Turn this back on' : 'Turn this off'}
                style={{
                  position: 'relative',
                  width: 64,
                  height: 36,
                  border: 'none',
                  borderRadius: 18,
                  background: row.when ? '#7c5cd6' : '#cbd2d9',
                  cursor: savingCursor ? 'wait' : 'pointer',
                  transition: 'background 0.15s',
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: 3,
                    left: row.when ? 31 : 3,
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                    transition: 'left 0.18s',
                  }}
                />
              </button>
            </div>
          ))}
        </div>

        {cursorMsg && (
          <div style={{ marginTop: 12, fontSize: 13, color: cursorMsg.startsWith('✓') ? '#198754' : '#dc3545' }}>{withCheck(cursorMsg)}</div>
        )}
      </div>
    </>
  );
}
